import * as React from 'react';

/**
 * Controls whose value a browser steps when the wheel scrolls over them while they are focused:
 * number and range inputs (Chrome, Firefox) and native selects (Firefox). Date/time inputs are
 * not stepped by any current browser, and MUI's popup Select/Autocomplete are plain divs.
 */
export const isWheelSteppable = (target: EventTarget | null): target is HTMLInputElement | HTMLSelectElement =>
  target instanceof HTMLSelectElement ||
  (target instanceof HTMLInputElement && (target.type === 'number' || target.type === 'range'));

/**
 * Browsers step a focused `<input type="number">` or native `<select>` when the wheel scrolls over
 * it, which silently edits caps, weights, percentages and dropdown choices while a user is only
 * scrolling a long form.
 *
 * One document-level listener covers every such control in the app, including the ones rendered
 * through redux-form / react-final-form wrappers whose own `slotProps` would discard a theme-level
 * `defaultProps.slotProps.htmlInput.onWheel`. Blurring inside the wheel event is enough: the
 * browser decides whether to step after handlers run, and an unfocused control is never stepped.
 *
 * Capture phase so nothing in the tree can stop the event before it gets here; passive because we
 * never call `preventDefault`, so the page still scrolls and Chrome keeps its scroll fast path.
 */
export function useBlurOnWheel() {
  React.useEffect(() => {
    const onWheel = (event: WheelEvent) => {
      const target = event.target;
      if (isWheelSteppable(target) && target === document.activeElement) {
        target.blur();
      }
    };
    document.addEventListener('wheel', onWheel, { capture: true, passive: true });
    return () => document.removeEventListener('wheel', onWheel, { capture: true });
  }, []);
}
